// Split from utils/luckyDraw.js so the form pages do not carry a CSPRNG. No React, so the offline
// checks can run it.

/** Uniform integer below `max`. Rejects the tail so the modulo cannot favour low values. */
const randomBelow = (max) => {
  const ceiling = Math.floor(0xffffffff / max) * max
  const buffer = new Uint32Array(1)
  let value

  do {
    crypto.getRandomValues(buffer)
    value = buffer[0]
  } while (value >= ceiling)

  return value % max
}

/** One card per ticket, in ticket order, so card N is ticket N. */
export const buildCards = (entries) => {
  const cards = []

  entries.forEach((entry, ordinal) => {
    for (let ticket = entry.from; ticket <= entry.to; ticket++) {
      cards.push({
        ticket,
        companyKey: entry.companyKey,
        companyName: entry.companyName,
        // Alternates per company, so one dealer's tickets read as a block.
        tint: ordinal % 2,
      })
    }
  })

  return cards
}

// Keeps a line of text off the far edges of a wide projection. Shared by the stage's notice and
// the winners board below it, so the two columns line up.
export const LIST_MAX_WIDTH = 1100

// A card's states, as class names. Shared vocabulary: the wall toggles them on the nodes, the
// card's sheet says what each looks like, and neither owns the other.
export const LIT_CLASS = 'lit'
export const WON_CLASS = 'won'
// On the grid only at rest, so the mouse cannot pull a card from under a flash.
export const INSPECT_CLASS = 'inspect'

// Spacing is the grid's gap, not a margin on each card: two margins meet and double it.
export const CARD_GAP_PX = 4
// Comes straight off the name's width, so a card 50px across cannot spare much.
export const CARD_PADDING_PX = 4
// Off the 4px grid: a 4px edge on a sub-40px card is a frame.
export const CARD_BORDER_PX = 1
// What the card takes from its own track. Gap is cut from the grid first, so it counts separately.
const CARD_INSET_X = (CARD_PADDING_PX + CARD_BORDER_PX) * 2
const CARD_INSET_Y = CARD_BORDER_PX * 2

export const NAME_LINES = 2
export const NAME_LINE_HEIGHT = 1.1
const MAX_FONT_PX = 13
// Below this a name is a smudge whatever it says, so the wall drops to a mosaic.
const MIN_FONT_PX = 5

// A name's cost across and down, in multiples of its own font size. 0.55em is the average advance
// of an uppercase sans character; 12 characters is enough to recognise your own name.
const PER_CHAR = 12 * 0.55
const PER_LINE = NAME_LINES * NAME_LINE_HEIGHT + 0.2

/**
 * Type that fits a card, as CSS. Sized off the grid, not off each card: a card's box is fractional,
 * so per-card units let neighbours round apart — ragged once the type is small.
 */
export const cardFontCss = (columns, rows) => {
  const track = (axis, count) => `(100cq${axis} - ${CARD_GAP_PX * (count - 1)}px) / ${count}`
  // toFixed, or 12 * 0.55 reaches the stylesheet as 6.6000000000000005.
  const byWidth = `calc((${track('w', columns)} - ${CARD_INSET_X}px) / ${PER_CHAR.toFixed(2)})`
  const byHeight = `calc((${track('h', rows)} - ${CARD_INSET_Y}px) / ${PER_LINE.toFixed(2)})`

  return `min(${byWidth}, ${byHeight}, ${MAX_FONT_PX}px)`
}

/** Grid sizes at which a name would resolve below MIN_FONT_PX, as container queries. */
export const mosaicQueries = (columns, rows) => [
  `(max-width: ${Math.round((MIN_FONT_PX * PER_CHAR + CARD_INSET_X) * columns + CARD_GAP_PX * (columns - 1))}px)`,
  `(max-height: ${Math.round((MIN_FONT_PX * PER_LINE + CARD_INSET_Y) * rows + CARD_GAP_PX * (rows - 1))}px)`,
]

/**
 * Every card not yet drawn, capped or not. What the flashes travel: a capped company's tickets
 * have to light up like any other, or never being chosen is itself the tell.
 */
export const undrawnCards = (cards, won) => {
  const undrawn = []

  for (let index = 0; index < cards.length; index++) {
    if (!won.has(cards[index].ticket)) undrawn.push(index)
  }

  return undrawn
}

/**
 * Of those, the ones that may actually win. Only the landing is confined to this list, which is
 * what keeps the draw fair without the restriction being visible while it runs.
 */
export const liveCards = (cards, headroom, won) =>
  undrawnCards(cards, won).filter((index) => headroom.get(cards[index].companyKey) > 0)

/**
 * `count` distinct cards from `pool`, uniformly chosen. The flashes, where nothing is at stake, so
 * no cap is applied and a capped company is as likely to light as any other.
 */
export const pickAny = (pool, count) => {
  const taken = new Set()

  // Rejection rather than a scan: count is at most ten against hundreds, so a clash is rare.
  while (taken.size < count && taken.size < pool.length) taken.add(pool[randomBelow(pool.length)])

  return [...taken]
}

/**
 * `count` distinct live cards, uniformly chosen. The landing, so the caps apply here. Returns short
 * rather than a card the server would refuse — an award is all-or-nothing.
 */
export const pickCards = (live, cards, headroom, count) => {
  const room = new Map(headroom)
  const taken = new Set()
  const picked = []

  for (let slot = 0; slot < count; slot++) {
    // Rebuilt per pick: the last one may have spent its company's allowance.
    const open = []

    for (const index of live) {
      if (!taken.has(index) && room.get(cards[index].companyKey) > 0) open.push(index)
    }

    if (open.length === 0) break

    const index = open[randomBelow(open.length)]
    const { companyKey } = cards[index]

    room.set(companyKey, room.get(companyKey) - 1)
    taken.add(index)
    picked.push(index)
  }

  return picked
}

/**
 * How long each flash of the stop is held, in order; only the rate slows. Here rather than in the
 * wall so the length can be asserted without a browser.
 */
export const flashPlan = (flickerMs, slowing, restMs) => {
  const plan = []
  let held = flickerMs

  while (held < restMs) {
    plan.push(held)
    held *= slowing
  }

  // Capped, not overshot: the ramp's next step would be half a second of dead air.
  plan.push(restMs)

  return plan
}
